import { Popover } from "@mui/material";

import { CardConfig } from "../DashboardHeader/DashboardParams";

import { CardEditFields } from "./CardEditFields";

export interface CardEditPopoverProps {
  /** The card being edited (controlled by the parent / URL). */
  card: CardConfig;
  /** All selectable metric names. */
  allNames: string[];
  /** The metric to show selected — the card's own, or its resolved fallback
   *  when unset/stale (see effectiveTrendMetric / effectiveChartMetric). */
  effectiveMetric: string;
  /** The card-menu button the popover anchors to. */
  anchorEl: HTMLElement;
  onClose: () => void;
  /** Fired with the updated card whenever the user changes a picker. */
  onChange: (next: CardConfig) => void;
}

/**
 * The "Edit Data" popover behind every card's ⋮ menu: {@link CardEditFields}
 * hung off the menu button. Stays open across edits (each change persists
 * through `onChange` immediately) and closes on click-away.
 *
 * Deliberately has no header or back affordance — it is reached directly from
 * one card, so there is nowhere to go back to. The add-card flow wraps the
 * same fields in a two-step surface that does.
 */
export function CardEditPopover({
  card,
  allNames,
  effectiveMetric,
  anchorEl,
  onClose,
  onChange,
}: CardEditPopoverProps) {
  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      // Popover's paper receives focus on open but has no intrinsic name, so
      // without this a screen reader announces nothing about what opened.
      slotProps={{ paper: { "aria-label": "Edit card data" } }}
    >
      <CardEditFields
        card={card}
        allNames={allNames}
        effectiveMetric={effectiveMetric}
        onChange={onChange}
      />
    </Popover>
  );
}
